import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { ErrorState, Loading } from '../ui/Page';
import { blockDate, getSchedule, saveSchedule, type WeeklyRule } from './api';

/**
 * When somebody may be booked.
 *
 * A week of rows rather than a list of rules, because that is the question being answered: "which
 * hours on a Tuesday". A day with no hours is a day nobody can book, which is why turning a day off
 * is the same gesture as clearing its hours rather than a separate switch.
 *
 * The whole week is sent on every save. The endpoint replaces the rules wholesale, and a form that
 * sent only what changed would have to reconstruct the rest — which is exactly how a Thursday
 * disappears.
 */

const WEEKDAYS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

/** The hours somebody who has never set a schedule almost certainly means. */
const DEFAULT_WEEK: WeeklyRule[] = [1, 2, 3, 4, 5].map((weekday) => ({
  weekday,
  startMinute: 9 * 60,
  endMinute: 18 * 60,
}));

export interface DayState {
  enabled: boolean;
  start: string;
  end: string;
}

export function AvailabilityPanel(): React.JSX.Element {
  const client = useQueryClient();
  const schedule = useQuery({ queryKey: ['availability'], queryFn: getSchedule });

  const [days, setDays] = useState<DayState[]>(() => emptyWeek());
  const [buffers, setBuffers] = useState({ before: 0, after: 0, notice: 60, ahead: 60 });
  const [blocked, setBlocked] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!schedule.data) return;

    const rules = schedule.data.rules.length > 0 ? schedule.data.rules : DEFAULT_WEEK;

    setDays(weekFrom(rules));

    if (schedule.data.schedule) {
      setBuffers({
        before: schedule.data.schedule.buffer_before_minutes,
        after: schedule.data.schedule.buffer_after_minutes,
        notice: schedule.data.schedule.min_notice_minutes,
        ahead: schedule.data.schedule.max_days_ahead,
      });
    }
  }, [schedule.data]);

  const save = useMutation({
    mutationFn: () =>
      saveSchedule({
        rules: rulesFrom(days),
        bufferBeforeMinutes: buffers.before,
        bufferAfterMinutes: buffers.after,
        minNoticeMinutes: buffers.notice,
        maxDaysAhead: buffers.ahead,
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void client.invalidateQueries({ queryKey: ['availability'] });
    },
    onError: (caught: unknown) => {
      setSaved(false);
      setError(messageFor(caught));
    },
  });

  const block = useMutation({
    mutationFn: () => blockDate({ date: blocked, available: false }),
    onSuccess: () => {
      setBlocked('');
      setError(null);
      void client.invalidateQueries({ queryKey: ['availability'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  useEffect(() => {
    if (!saved) return;

    const timer = setTimeout(() => setSaved(false), 2500);

    return () => clearTimeout(timer);
  }, [saved]);

  if (schedule.isLoading) return <Loading />;
  if (schedule.error) {
    return <ErrorState error={schedule.error} onRetry={() => void schedule.refetch()} />;
  }

  function update(index: number, changes: Partial<DayState>): void {
    setDays((current) =>
      current.map((day, position) => (position === index ? { ...day, ...changes } : day)),
    );
  }

  return (
    <div className="stack">
      <div className="availability">
        {days.map((day, index) => (
          <div key={WEEKDAYS[index]} className="availability__day">
            <label className="availability__toggle">
              <input
                type="checkbox"
                checked={day.enabled}
                onChange={(event) => update(index, { enabled: event.target.checked })}
              />
              <span>{WEEKDAYS[index]}</span>
            </label>

            {day.enabled ? (
              <span className="cell-inline">
                <input
                  type="time"
                  value={day.start}
                  onChange={(event) => update(index, { start: event.target.value })}
                />
                <span className="muted">—</span>
                <input
                  type="time"
                  value={day.end}
                  onChange={(event) => update(index, { end: event.target.value })}
                />
              </span>
            ) : (
              <span className="muted small">{t('calendar.dayOff')}</span>
            )}
          </div>
        ))}
      </div>

      <div className="form-grid">
        <label className="field">
          <span>{t('calendar.bufferBefore')}</span>
          <input
            type="number"
            min={0}
            max={240}
            value={buffers.before}
            onChange={(event) => setBuffers({ ...buffers, before: Number(event.target.value) })}
          />
        </label>

        <label className="field">
          <span>{t('calendar.bufferAfter')}</span>
          <input
            type="number"
            min={0}
            max={240}
            value={buffers.after}
            onChange={(event) => setBuffers({ ...buffers, after: Number(event.target.value) })}
          />
        </label>

        <label className="field">
          <span>{t('calendar.minNotice')}</span>
          <input
            type="number"
            min={0}
            max={43200}
            value={buffers.notice}
            onChange={(event) => setBuffers({ ...buffers, notice: Number(event.target.value) })}
          />
        </label>

        <label className="field">
          <span>{t('calendar.maxDaysAhead')}</span>
          <input
            type="number"
            min={1}
            max={365}
            value={buffers.ahead}
            onChange={(event) => setBuffers({ ...buffers, ahead: Number(event.target.value) })}
          />
        </label>
      </div>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="cell-inline">
        <button
          type="button"
          className="button button--primary button--compact"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? t('common.saving') : t('calendar.saveAvailability')}
        </button>

        {saved && (
          <span className="muted small" role="status">
            {t('prefs.saved')}
          </span>
        )}
      </div>

      <section className="editor__section">
        <h3>{t('calendar.blockedDates')}</h3>

        {schedule.data?.overrides.filter((each) => !each.available).length === 0 ? (
          <p className="muted small">{t('calendar.noBlockedDates')}</p>
        ) : (
          <ul className="chips">
            {(schedule.data?.overrides ?? [])
              .filter((each) => !each.available)
              .map((each) => (
                <li key={each.date} className="chip">
                  {each.date}
                </li>
              ))}
          </ul>
        )}

        <form
          className="cell-inline"
          onSubmit={(event) => {
            event.preventDefault();
            if (blocked) block.mutate();
          }}
        >
          <input type="date" value={blocked} onChange={(event) => setBlocked(event.target.value)} />
          <button
            type="submit"
            className="button button--quiet button--compact"
            disabled={!blocked || block.isPending}
          >
            {t('calendar.blockDate')}
          </button>
        </form>
      </section>
    </div>
  );
}

export function emptyWeek(): DayState[] {
  return WEEKDAYS.map(() => ({ enabled: false, start: '09:00', end: '18:00' }));
}

/** The stored rules as a week of rows. One row per day: a second window is not a thing yet. */
export function weekFrom(rules: WeeklyRule[]): DayState[] {
  const week = emptyWeek();

  for (const rule of rules) {
    const day = week[rule.weekday];

    if (!day) continue;

    day.enabled = true;
    day.start = minutesToTime(rule.startMinute);
    day.end = minutesToTime(rule.endMinute);
  }

  return week;
}

/** And back. A day whose end is not after its start is not a day anybody can book. */
export function rulesFrom(days: DayState[]): WeeklyRule[] {
  return days.flatMap((day, weekday) => {
    if (!day.enabled) return [];

    const startMinute = timeToMinutes(day.start);
    const endMinute = timeToMinutes(day.end);

    return endMinute > startMinute ? [{ weekday, startMinute, endMinute }] : [];
  });
}

function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);

  return `${String(hours).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

function timeToMinutes(value: string): number {
  const [hours, minutes] = value.split(':').map(Number);

  return (hours ?? 0) * 60 + (minutes ?? 0);
}
